import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import axios from "axios";
import Header from "../components/Header";
import "../styles/global.css";
import "../styles/quiz.css";
import { Helmet } from "react-helmet";
// Enhanced question set with additional context and emotional triggers
const questions = [
  {
    id: 1,
    question: "Does your partner respect your opinions and decisions?",
    description:
      "Respect is a cornerstone of healthy relationships. This includes valuing your perspective, even when disagreeing.",
    options: ["Always", "Often", "Sometimes", "Rarely", "Never"],
  },
  {
    id: 2,
    question:
      "How comfortable are you sharing vulnerable thoughts and feelings with your partner?",
    description:
      "Emotional safety allows for authentic connection. Consider how freely you can express yourself without fear of judgment.",
    options: [
      "Completely comfortable",
      "Mostly comfortable",
      "Sometimes comfortable",
      "Rarely comfortable",
      "Never comfortable",
    ],
  },
  {
    id: 3,
    question:
      "Does your partner actively support your personal goals and growth?",
    description:
      "A loving partner encourages your individual aspirations and celebrates your successes.",
    options: [
      "Always supports me",
      "Often supports me",
      "Sometimes supports me",
      "Rarely supports me",
      "Actively discourages me",
    ],
  },
  {
    id: 4,
    question: "How much do you trust your partner to be honest and faithful?",
    description:
      "Trust is the foundation of intimacy. Consider both your partner's actions and how secure you feel in the relationship.",
    options: [
      "Complete trust",
      "Strong trust with minor doubts",
      "Moderate trust",
      "Significant trust issues",
      "No trust at all",
    ],
  },
  {
    id: 5,
    question: "Do you feel emotionally safe and secure with your partner?",
    description:
      "Emotional security means feeling accepted, valued, and free from fear of emotional harm.",
    options: ["Always", "Often", "Sometimes", "Rarely", "Never"],
  },
  {
    id: 6,
    question: "How effectively do you and your partner resolve conflicts?",
    description:
      'Healthy conflict resolution involves listening, compromise, and finding solutions rather than "winning" arguments.',
    options: [
      "Very effectively with mutual respect",
      "Generally well with occasional tension",
      "Inconsistently - some issues remain unresolved",
      "Poorly - with recurring arguments",
      "Conflicts are never truly resolved",
    ],
  },
  {
    id: 7,
    question: "How valued and appreciated do you feel in your relationship?",
    description:
      "Feeling appreciated involves recognition of your contributions, qualities, and presence in your partner's life.",
    options: [
      "Deeply valued every day",
      "Generally appreciated",
      "Occasionally acknowledged",
      "Rarely recognized",
      "Completely taken for granted",
    ],
  },
  {
    id: 8,
    question:
      "How aligned are your core values and life goals with your partner?",
    description:
      "Value alignment affects long-term compatibility on important life decisions and priorities.",
    options: [
      "Perfectly aligned",
      "Mostly aligned with compromise possible",
      "Some significant differences",
      "Fundamentally misaligned",
      "Completely opposed directions",
    ],
  },
  {
    id: 9,
    question:
      "How much effort does your partner put into strengthening your relationship?",
    description:
      "Relationships require ongoing investment from both partners to grow and thrive.",
    options: [
      "Consistent meaningful effort",
      "Regular effort",
      "Occasional effort",
      "Minimal effort",
      "No effort at all",
    ],
  },
  {
    id: 10,
    question:
      "How often do you feel genuinely happy and fulfilled in your relationship?",
    description:
      "While all relationships have ups and downs, overall fulfillment is essential for long-term satisfaction.",
    options: ["Most of the time", "Often", "Sometimes", "Rarely", "Never"],
  },
  {
    id: 11,
    question: "Do you feel physically safe with your partner at all times?",
    description:
      "Physical safety is non-negotiable in healthy relationships. Any form of physical intimidation or harm is a serious red flag.",
    options: [
      "Always completely safe",
      "Generally safe with rare tension",
      "Sometimes unsafe",
      "Often unsafe",
      "Never feel physically safe",
    ],
  },
  {
    id: 12,
    question:
      "When you envision your future, how clearly do you see your partner in it?",
    description:
      "Your intuition about your shared future can reveal deeper feelings about the relationship's potential.",
    options: [
      "Clearly see a fulfilling future together",
      "Mostly see a positive future",
      "Uncertain about our future",
      "Difficulty imagining a happy future together",
      "Cannot see a future with this person",
    ],
  },
  {
    id: 13,
    question: "How do you feel about yourself when you're with your partner?",
    description:
      "Healthy relationships tend to bring out our best selves and encourage positive self-image.",
    options: [
      "Better and more confident",
      "Generally good about myself",
      "Sometimes diminished",
      "Often inadequate",
      "Consistently worse about myself",
    ],
  },
  {
    id: 14,
    question: "How balanced is the give and take in your relationship?",
    description:
      "While perfect balance isn't always possible, overall reciprocity is important for relationship health.",
    options: [
      "Very balanced",
      "Generally fair",
      "Somewhat one-sided",
      "Significantly imbalanced",
      "Completely one-sided",
    ],
  },
  {
    id: 15,
    question: "How often does your relationship bring you joy versus stress?",
    description:
      "While all relationships face challenges, the overall balance of positive to negative experiences matters.",
    options: [
      "Much more joy than stress",
      "More joy than stress",
      "Equal joy and stress",
      "More stress than joy",
      "Almost entirely stressful",
    ],
  },
];

const motivationalQuotes = [
  "Self-awareness is the first step toward positive change.",
  "Honest reflection creates space for authentic happiness.",
  "Understanding your relationship needs is an act of self-care.",
  "Trust your intuition—it's your inner wisdom speaking.",
  "Your feelings are valid messengers deserving of attention.",
  "Clarity comes from engagement, not avoidance.",
  "Every answer brings you closer to understanding what you truly need.",
  "The courage to face relationship truths leads to greater peace.",
  "Taking time to reflect shows respect for yourself and your partner.",
  "Your relationship deserves your honest attention and evaluation.",
];

const Quiz = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showDescription, setShowDescription] = useState(false);
  const [quote, setQuote] = useState("");
  const [showAdBreak, setShowAdBreak] = useState(false);
  const controls = useAnimation();
  const contentRef = useRef(null);

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const canGoBack = currentQuestion > 0;
  const isLastQuestion = currentQuestion === questions.length - 1;

  useEffect(() => {
    // Select a random motivational quote
    document.title = "Relationship Wellness Quiz";
    const randomQuote =
      motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)];
    setQuote(randomQuote);

    // Reset the selected option when changing questions
    setSelectedOption(null);

    // Reset description visibility
    setShowDescription(false);

    // Animate the new question
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    });

    // Show ad break after every 5 questions (except the first)
    if (currentQuestion > 0 && currentQuestion % 5 === 0) {
      setShowAdBreak(true);

      // Initialize AdSense ads
      try {
        if (window.adsbygoogle && process.env.NODE_ENV !== "development") {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        }
      } catch (e) {
        console.error("AdSense error:", e);
      }
    } else {
      setShowAdBreak(false);
    }

    // Scroll to top of content when question changes
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentQuestion, controls]);

  const handleAnswer = (option) => {
    setSelectedOption(option);
  };

  const confirmAnswer = () => {
    if (selectedOption) {
      const newAnswers = [...answers];
      newAnswers[currentQuestion] = {
        question: questions[currentQuestion].question,
        answer: selectedOption,
        questionId: questions[currentQuestion].id,
      };
      setAnswers(newAnswers);

      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        submitAnswers(newAnswers.filter((answer) => answer !== undefined));
      }
    }
  };

  const submitAnswers = async (finalAnswers) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        "https://us-central1-should-i-breakup-or-stay.cloudfunctions.net/api/api/analyze",
        { answers: finalAnswers }
      );
      navigate("/result", { state: { result: response.data } });
    } catch (error) {
      console.error("Submission error:", error);
      // Fallback to navigate anyway with mock data if server is down
      navigate("/result", {
        state: {
          result: {
            analysis:
              "We couldn't process your answers completely, but based on your responses, we've detected some relationship concerns that merit attention.",
            score: 65,
            recommendations: [
              "Consider discussing your feelings with a trusted friend or therapist",
              "Reflect on the patterns you've noticed in your answers",
              "Take time for self-care while you process this information",
            ],
            error: error.message,
          },
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = () => {
    if (canGoBack) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const getOptionColor = (index) => {
    const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#ef4444"];
    return colors[index % colors.length];
  };

  const isOptionSelected = (option) => {
    return selectedOption === option;
  };

  const handleContinueFromAd = () => {
    setShowAdBreak(false);
  };

  return (
    <div className="quiz-container">
      <div className="quiz-background"></div>
      <Header />
      <Helmet>
        <title>Relationship Quiz: Should You Break Up?</title>
        <meta
          name="description"
          content="Take our relationship quiz to assess your relationship health and decide if you should break up."
        />
      </Helmet>
      <div className="content-container" ref={contentRef}>
        <div className="quiz-header">
          <div className="quiz-progress-info">
            <div className="quiz-navigation">
              <button
                onClick={handlePrevious}
                disabled={!canGoBack}
                className={`quiz-nav-button ${!canGoBack ? "disabled" : ""}`}
                aria-label="Go to previous question">
                <i className="fas fa-arrow-left"></i>
                <span>Previous</span>
              </button>

              <Link to="/" className="quiz-exit-button">
                <i className="fas fa-times"></i>
                <span>Exit Quiz</span>
              </Link>
            </div>

            <div className="quiz-progress-container">
              <div className="quiz-progress-bar">
                <motion.div
                  className="quiz-progress-fill"
                  style={{ width: `${progress}%` }}
                  initial={{
                    width: `${(currentQuestion / questions.length) * 100}%`,
                  }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
              <div className="quiz-progress-text">
                <span className="quiz-progress-percentage">
                  {Math.round(progress)}% Complete
                </span>
                <span className="quiz-progress-count">
                  Question {currentQuestion + 1} of {questions.length}
                </span>
              </div>
            </div>
          </div>

          <div className="quiz-quote-container">
            <div className="quiz-quote">
              <i className="fas fa-quote-left"></i>
              <p>{quote}</p>
              <i className="fas fa-quote-right"></i>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {showAdBreak ? (
            <motion.div
              className="quiz-ad-break"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              key="ad-break">
              <div className="quiz-ad-break-header">
                <h2>Taking a moment to reflect</h2>
                <p>
                  You're doing great! Take a brief pause to consider your
                  responses so far.
                </p>
              </div>

              <div
                className="ad-container in-quiz-ad"
                style={{ width: "100%", minWidth: "300px" }}>
                {/* <div className="ad-label">Advertisement</div> */}
                <ins
                  className="adsbygoogle"
                  style={{ display: "block" }}
                  data-ad-client="ca-pub-2067522743161930"
                  data-ad-slot="1234567890"
                  data-ad-format="auto"
                  data-full-width-responsive="true"></ins>
              </div>

              <button
                className="quiz-continue-button"
                onClick={handleContinueFromAd}>
                Continue Assessment
                <i className="fas fa-arrow-right"></i>
              </button>
            </motion.div>
          ) : (
            <motion.div
              className="quiz-question-section"
              key={`question-${currentQuestion}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}>
              <div className="quiz-question">
                <h2>{questions[currentQuestion].question}</h2>
                <motion.button
                  className="question-info-button"
                  onClick={() => setShowDescription(!showDescription)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}>
                  <i
                    className={`fas fa-${
                      showDescription ? "minus" : "info"
                    }-circle`}>
                    ?
                  </i>
                </motion.button>
              </div>

              <AnimatePresence>
                {showDescription && (
                  <motion.div
                    className="question-description"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}>
                    <p>{questions[currentQuestion].description}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="quiz-options">
                {questions[currentQuestion].options.map((option, index) => (
                  <motion.button
                    key={index}
                    className={`quiz-option ${
                      isOptionSelected(option) ? "selected" : ""
                    }`}
                    style={{
                      "--option-color": getOptionColor(index),
                    }}
                    onClick={() => handleAnswer(option)}
                    whileHover={{
                      scale: 1.02,
                      boxShadow: `0 5px 15px ${getOptionColor(index)}30`,
                    }}
                    whileTap={{ scale: 0.98 }}>
                    <span className="option-content">
                      <span className="option-text">{option}</span>
                      {isOptionSelected(option) && (
                        <motion.span
                          className="option-check"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 15,
                          }}>
                          <i className="fas fa-check"></i>
                        </motion.span>
                      )}
                    </span>
                  </motion.button>
                ))}
              </div>

              <motion.button
                className={`quiz-next-button ${
                  !selectedOption ? "disabled" : ""
                }`}
                onClick={confirmAnswer}
                disabled={!selectedOption}
                whileHover={selectedOption ? { scale: 1.03 } : {}}
                whileTap={selectedOption ? { scale: 0.97 } : {}}>
                <span>{isLastQuestion ? "Submit Answers" : "Continue"}</span>
                <i
                  className={`fas fa-${
                    isLastQuestion ? "check" : "arrow-right"
                  }`}></i>
              </motion.button>

              {/* Bottom ad */}
              <div
                className="ad-container bottom-quiz-ad"
                style={{ width: "100%", minWidth: "300px" }}>
                {/* <div className="ad-label">Advertisement</div> */}
                <ins
                  className="adsbygoogle"
                  style={{ display: "block" }}
                  data-ad-client="ca-pub-2067522743161930"
                  data-ad-slot="1234567891"
                  data-ad-format="auto"
                  data-full-width-responsive="true"></ins>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Support notice */}
        <div className="quiz-support-notice">
          <i className="fas fa-heart"></i>
          <div>
            <p>
              If you're experiencing distress or feel unsafe in your
              relationship, please reach out for help.
            </p>
            <a
              href="https://www.thehotline.org/"
              target="_blank"
              rel="noopener noreferrer">
              National Domestic Violence Hotline: 1-800-799-7233
            </a>
          </div>
        </div>
      </div>

      {/* Loading overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            className="loading-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}>
            <div className="loading-container">
              <div className="loading-animation">
                <div className="loading-circle"></div>
                <div className="loading-circle"></div>
                <div className="loading-circle"></div>
              </div>
              <h3>Analyzing Your Responses</h3>
              <p>
                Our AI is processing your answers to provide personalized
                insights...
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Quiz;
